import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { useApi } from '../hooks/useApi';
import {
  PRICE_SANE_MAX,
  PRICE_SANE_MIN,
  REASON_LABELS,
  type ReviewRow,
} from '../services/reviewService';
import CheckIcon from '@mui/icons-material/Check';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import CloseIcon from '@mui/icons-material/Close';
import SaveIcon from '@mui/icons-material/Save';
import EditIcon from '@mui/icons-material/Edit';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import ThumbUpIcon from '@mui/icons-material/ThumbUp';
import ThumbDownIcon from '@mui/icons-material/ThumbDown';
import SearchIcon from '@mui/icons-material/Search';

const PAGE_SIZE = 20;

const BANDS: { value: string; label: string }[] = [
  { value: 'low', label: 'Below 60% of ours' },
  { value: 'in', label: 'Within 60-160%' },
  { value: 'high', label: 'Above 160% of ours' },
  { value: 'unknown', label: 'Ratio unknown' },
];

const REASON_OPTIONS = Object.entries(REASON_LABELS).map(([value, label]) => ({ value, label }));

const money = (v: number | null | undefined) =>
  v === null || v === undefined
    ? '—'
    : '₹' + Number(v).toLocaleString('en-IN', { minimumFractionDigits: 0, maximumFractionDigits: 2 });

const stripWww = (s: string) => (s || '').replace(/^www\./i, '');

const dateLabel = (v: string | null) => {
  if (!v) return 'never';
  const d = new Date(v);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
};

const FilterSelect: React.FC<{
  value: string;
  onChange: (v: string) => void;
  options: { value: string; label: string }[];
  placeholder: string;
}> = ({ value, onChange, options, placeholder }) => (
  <div className="relative min-w-0 sm:min-w-[140px]">
    <select
      value={value}
      onChange={(e) => onChange(e.target.value)}
      className="appearance-none w-full bg-surface border border-border-subtle rounded-lg px-3 py-2 pr-8 text-body-xs sm:text-body-sm text-on-surface focus:outline-none focus:ring-2 focus:ring-primary/20 cursor-pointer"
    >
      <option value="">{placeholder}</option>
      {options.map((o) => (
        <option key={o.value} value={o.value}>
          {o.label}
        </option>
      ))}
    </select>
    <ExpandMoreIcon className="absolute right-2 top-1/2 -translate-y-1/2 text-on-surface-variant text-[16px] pointer-events-none" />
  </div>
);

const Review: React.FC = () => {
  const api = useApi();

  const [rows, setRows] = useState<ReviewRow[]>([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; kind: 'ok' | 'warn' } | null>(null);
  const [selected, setSelected] = useState<Set<number>>(new Set());
  const [selectedAll, setSelectedAll] = useState(false);

  const [domain, setDomain] = useState('');
  const [category, setCategory] = useState('');
  const [reason, setReason] = useState('');
  const [band, setBand] = useState('');
  const [q, setQ] = useState('');
  const [search, setSearch] = useState('');

  const [competitors, setCompetitors] = useState<string[]>([]);
  const [categories, setCategories] = useState<string[]>([]);

  const [editing, setEditing] = useState<ReviewRow | null>(null);
  const [editUrl, setEditUrl] = useState('');
  const [editSelector, setEditSelector] = useState('');
  const [editStockSelector, setEditStockSelector] = useState('');

  const showToast = useCallback((msg: string, kind: 'ok' | 'warn' = 'ok') => {
    setToast({ msg, kind });
    setTimeout(() => setToast(null), 5000);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.review.list({
        page,
        limit: PAGE_SIZE,
        domain: domain || undefined,
        category: category || undefined,
        reason: reason || undefined,
        band: (band || undefined) as 'low' | 'in' | 'high' | 'unknown' | undefined,
        q: search || undefined,
      });
      setRows(res.rows || []);
      setTotal(res.total || 0);
      setError(null);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [api, page, domain, category, reason, band, search]);

  useEffect(() => {
    load();
  }, [load]);

  // The search box talks to the server on every keystroke otherwise.
  useEffect(() => {
    const t = setTimeout(() => {
      setSearch(q.trim());
      setPage(1);
    }, 350);
    return () => clearTimeout(t);
  }, [q]);

  // A new filter set means a different row set - a stale selection would act on the wrong rows.
  useEffect(() => {
    setSelected(new Set());
    setSelectedAll(false);
  }, [domain, category, reason, band, search]);

  useEffect(() => {
    api.comparison
      .competitors()
      .then((res) => setCompetitors(res.competitors || []))
      .catch(() => setCompetitors([]));
    api.comparison
      .categories()
      .then((res) => setCategories(res.categories || []))
      .catch(() => setCategories([]));
  }, [api]);

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const toggleSelect = (id: number) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const clearSelection = () => {
    setSelected(new Set());
    setSelectedAll(false);
  };

  /** Select-all reaches across pages: the server returns every id matching the current filters. */
  const selectAllMatching = async () => {
    setBusy(true);
    try {
      const res = await api.review.ids({
        domain: domain || undefined,
        category: category || undefined,
        reason: reason || undefined,
        band: (band || undefined) as 'low' | 'in' | 'high' | 'unknown' | undefined,
        q: search || undefined,
      });
      setSelected(new Set(res.ids || []));
      setSelectedAll(true);
    } catch (err) {
      showToast((err as Error).message, 'warn');
    } finally {
      setBusy(false);
    }
  };

  const approve = async (ids: number[]) => {
    if (ids.length === 0) return;
    setBusy(true);
    try {
      const res = await api.review.approve(ids);
      const blocked = res.unscrapeable || [];
      if (blocked.length > 0) {
        const listed = blocked
          .slice(0, 5)
          .map((b) => `#${b.id} ${b.reason === 'no_url' ? 'no url' : 'no selector'}`)
          .join(', ');
        const rest = blocked.length > 5 ? ` +${blocked.length - 5} more` : '';
        showToast(
          `${res.approved} approved — ${blocked.length} row(s) will never scrape: ${listed}${rest}`,
          'warn'
        );
      } else {
        showToast(`${res.approved} approved — picked up by the next scrape pass`);
      }
      setSelected(new Set());
      await load();
    } catch (err) {
      showToast((err as Error).message, 'warn');
    } finally {
      setBusy(false);
    }
  };

  const reject = async (ids: number[]) => {
    if (ids.length === 0) return;
    setBusy(true);
    try {
      const res = await api.review.reject(ids);
      showToast(`${res.rejected} marked not sold`);
      setSelected(new Set());
      await load();
    } catch (err) {
      showToast((err as Error).message, 'warn');
    } finally {
      setBusy(false);
    }
  };

  const openEdit = (row: ReviewRow) => {
    setEditing(row);
    setEditUrl(row.url || '');
    setEditSelector(row.selector || '');
    setEditStockSelector(row.stock_selector || '');
  };

  const saveEdit = async () => {
    if (!editing) return;
    setBusy(true);
    try {
      const res = await api.review.update(editing.id, {
        url: editUrl.trim(),
        selector: editSelector.trim(),
        stock_selector: editStockSelector.trim(),
      });
      setEditing(null);
      if ((res.unscrapeable || []).length > 0) {
        showToast(`Saved and approved — row ${editing.id} still has no url or selector`, 'warn');
      } else {
        showToast(`Saved and approved — picked up by the next scrape pass`);
      }
      await load();
    } catch (err) {
      showToast((err as Error).message, 'warn');
    } finally {
      setBusy(false);
    }
  };

  const ratioCell = (row: ReviewRow) => {
    if (row.ratio === null || row.ratio === undefined) {
      return <span className="text-xs text-on-surface-variant">unknown</span>;
    }
    const out = row.ratio < PRICE_SANE_MIN || row.ratio > PRICE_SANE_MAX;
    return (
      <span
        className={`font-data-mono text-xs ${out ? 'text-danger' : 'text-success'}`}
        title={row.ratio_source === 'review' ? 'Ratio seen by the resolver' : 'Ratio from the last scrape log'}
      >
        {Math.round(row.ratio * 100)}%
      </span>
    );
  };

  const filterOptions = useMemo(
    () => ({
      domain: competitors.map((c) => ({ value: stripWww(c).toLowerCase(), label: stripWww(c) })),
      category: categories.map((c) => ({ value: c, label: c })),
    }),
    [competitors, categories]
  );

  return (
    <div className="space-y-4 px-4 mt-4">
      {toast && typeof document !== 'undefined' && createPortal(
        <div className="fixed top-4 right-4 z-50 bg-sidebar-bg text-white px-4 py-3 rounded-xl shadow-xl border border-primary-fixed-dim/30 flex items-center gap-2 animate-toast-in max-w-[90vw]">
          {toast.kind === 'ok' ? (
            <CheckIcon className="text-success text-[16px] sm:text-[20px]" />
          ) : (
            <WarningIcon className="text-warning text-[16px] sm:text-[20px]" />
          )}
          <span className="text-xs font-medium">{toast.msg}</span>
        </div>,
        document.body
      )}

      <div className="bg-surface border border-border-subtle rounded-xl p-3 sm:p-5">
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 mb-3">
          <div>
            <h2 className="font-headline-sm text-on-surface">Review queue</h2>
            <p className="text-body-xs text-on-surface-variant">
              Links the link-fixer sent to review. Approving queues the row for the next scrape pass — it does not
              scrape immediately.
            </p>
          </div>
          <span className="text-body-xs text-on-surface-variant">{total} waiting</span>
        </div>

        <div className="flex flex-col lg:flex-row gap-2">
          <FilterSelect
            value={domain}
            onChange={(v) => {
              setDomain(v);
              setPage(1);
            }}
            options={filterOptions.domain}
            placeholder="Any competitor"
          />
          <FilterSelect
            value={category}
            onChange={(v) => {
              setCategory(v);
              setPage(1);
            }}
            options={filterOptions.category}
            placeholder="Any category"
          />
          <FilterSelect
            value={reason}
            onChange={(v) => {
              setReason(v);
              setPage(1);
            }}
            options={REASON_OPTIONS}
            placeholder="Any reason"
          />
          <FilterSelect
            value={band}
            onChange={(v) => {
              setBand(v);
              setPage(1);
            }}
            options={BANDS}
            placeholder="Any ratio"
          />
          <div className="relative flex-1 min-w-0">
            <SearchIcon className="absolute left-2.5 top-1/2 -translate-y-1/2 text-on-surface-variant text-[14px] pointer-events-none" />
            <input
              type="search"
              placeholder="Search product, sku or competitor..."
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="w-full pl-8 pr-3 bg-surface border border-border-subtle rounded-lg px-3 py-2 text-body-xs sm:text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </div>
        </div>
      </div>

      {selected.size > 0 && (
        <div className="bg-primary-container text-on-primary px-3 sm:px-4 py-2.5 rounded-xl flex flex-wrap items-center gap-2">
          <span className="text-body-xs sm:text-body-sm font-semibold">
            {selected.size} selected{selectedAll ? ' (all matching)' : ''}
          </span>
          <div className="flex flex-wrap items-center gap-1.5 ml-auto">
            <button
              onClick={() => approve(Array.from(selected))}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-primary text-white text-body-xs font-semibold hover:opacity-90 disabled:opacity-50 flex items-center gap-1"
            >
              <ThumbUpIcon className="text-[14px]" />
              Approve
            </button>
            <button
              onClick={() => reject(Array.from(selected))}
              disabled={busy}
              className="px-3 py-1.5 rounded-lg bg-surface-high text-on-surface text-body-xs font-semibold hover:opacity-90 disabled:opacity-50 flex items-center gap-1"
            >
              <ThumbDownIcon className="text-[14px]" />
              Not sold
            </button>
          </div>
        </div>
      )}

      <div className="bg-surface border border-border-subtle rounded-xl overflow-hidden shadow-2xs min-w-0">
        {error && (
          <div className="p-3 sm:p-4 bg-danger/10 border-b border-danger/20 text-danger text-body-xs sm:text-body-sm flex items-center gap-2">
            <ErrorIcon className="text-[14px] sm:text-[16px]" />
            {error}
          </div>
        )}

        <div className="overflow-x-auto min-w-0">
          <table className="w-full">
            <thead>
              <tr className="bg-surface-low border-b border-border-subtle">
                <th className="w-9 px-1.5 sm:px-4 py-2.5">
                  <input
                    type="checkbox"
                    checked={selectedAll || (rows.length > 0 && rows.every((r) => selected.has(r.id)))}
                    onChange={(e) => {
                      if (e.target.checked) selectAllMatching();
                      else clearSelection();
                    }}
                    disabled={busy}
                    className="rounded border-border-subtle"
                  />
                </th>
                <th className="text-left text-[10px] font-label-caps text-on-surface-variant uppercase px-1.5 sm:px-4 py-2.5">Product</th>
                <th className="text-left text-[10px] font-label-caps text-on-surface-variant uppercase px-1.5 sm:px-4 py-2.5">Competitor</th>
                <th className="text-left text-[10px] font-label-caps text-on-surface-variant uppercase px-1.5 sm:px-4 py-2.5">Price</th>
                <th className="text-left text-[10px] font-label-caps text-on-surface-variant uppercase px-1.5 sm:px-4 py-2.5 hidden md:table-cell">Reason</th>
                <th className="text-left text-[10px] font-label-caps text-on-surface-variant uppercase px-1.5 sm:px-4 py-2.5 hidden sm:table-cell">Link</th>
                <th className="text-left text-[10px] font-label-caps text-on-surface-variant uppercase px-1.5 sm:px-4 py-2.5 hidden lg:table-cell">Last checked</th>
                <th className="text-right text-[10px] font-label-caps text-on-surface-variant uppercase px-1.5 sm:px-4 py-2.5">Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={8} className="text-center py-12">
                    <div className="flex justify-center">
                      <div className="animate-spin rounded-full h-6 w-6 border-2 border-primary border-t-transparent" />
                    </div>
                  </td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={8}>
                    <div className="flex flex-col items-center justify-center py-12 sm:py-16">
                      <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-full bg-surface-container-high flex items-center justify-center mb-3">
                        <FactCheckIcon className="text-on-surface-variant text-[20px] sm:text-[24px]" />
                      </div>
                      <p className="text-on-surface-variant font-body-xs sm:text-body-sm">Nothing to review</p>
                      <p className="text-on-surface-variant/60 text-xs mt-1 hidden sm:block">
                        Links the link-fixer cannot confirm land here.
                      </p>
                    </div>
                  </td>
                </tr>
              ) : (
                rows.map((row) => (
                  <tr key={row.id} className="border-b border-border-subtle/50 hover:bg-surface-low transition-colors">
                    <td className="px-1.5 sm:px-4 py-2">
                      <input
                        type="checkbox"
                        checked={selected.has(row.id)}
                        onChange={() => toggleSelect(row.id)}
                        className="rounded border-border-subtle"
                      />
                    </td>
                    <td className="px-1.5 sm:px-4 py-2 max-w-[180px]">
                      <div className="text-body-xs sm:text-body-sm text-on-surface truncate" title={row.prod_title || ''}>
                        {row.prod_title || `Product ${row.prod_id}`}
                      </div>
                      <div className="font-data-mono text-[10px] text-on-surface-variant">
                        ours {money(row.our_price)}
                        {row.sku ? ` · ${row.sku}` : ''}
                      </div>
                    </td>
                    <td className="px-1.5 sm:px-4 py-2 text-body-xs sm:text-body-sm text-on-surface">
                      <div className="max-w-[140px] truncate">{stripWww(row.title)}</div>
                      {row.siblings.length > 0 && (
                        <span
                          className="text-[10px] text-on-surface-variant"
                          title={row.siblings.map((s) => `#${s.id} ${s.url || ''}`).join('\n')}
                        >
                          {row.siblings.length} duplicate link(s)
                        </span>
                      )}
                    </td>
                    <td className="px-1.5 sm:px-4 py-2">
                      <div className="font-data-mono text-body-xs sm:text-body-sm text-on-surface">{money(row.comp_price)}</div>
                      <div>{ratioCell(row)}</div>
                    </td>
                    <td className="px-1.5 sm:px-4 py-2 hidden md:table-cell max-w-[220px]">
                      <div className="text-body-xs text-on-surface">{row.reason ? REASON_LABELS[row.reason] || row.reason : '—'}</div>
                      {row.reason_detail && (
                        <div className="text-[10px] text-on-surface-variant truncate" title={row.reason_detail}>
                          {row.reason_detail}
                        </div>
                      )}
                    </td>
                    <td className="px-1.5 sm:px-4 py-2 hidden sm:table-cell">
                      {row.url ? (
                        <a
                          href={row.url}
                          target="_blank"
                          rel="noopener noreferrer"
                          title={row.url}
                          className="inline-flex items-center text-primary hover:underline"
                        >
                          <OpenInNewIcon className="text-[14px] sm:text-[16px]" />
                        </a>
                      ) : (
                        <span className="text-on-surface-variant text-xs">—</span>
                      )}
                    </td>
                    <td className="px-1.5 sm:px-4 py-2 text-body-xs text-on-surface-variant hidden lg:table-cell whitespace-nowrap">
                      {dateLabel(row.last_checked)}
                    </td>
                    <td className="px-1.5 sm:px-4 py-2">
                      <div className="flex justify-end gap-0.5 sm:gap-1">
                        <button
                          onClick={() => approve([row.id])}
                          disabled={busy}
                          className="p-1 sm:p-1.5 rounded hover:bg-surface-high transition-colors disabled:opacity-40"
                          title="Approve"
                        >
                          <ThumbUpIcon className="text-[12px] sm:text-[16px] text-success" />
                        </button>
                        <button
                          onClick={() => reject([row.id])}
                          disabled={busy}
                          className="p-1 sm:p-1.5 rounded hover:bg-surface-high transition-colors disabled:opacity-40"
                          title="Not sold"
                        >
                          <ThumbDownIcon className="text-[12px] sm:text-[16px] text-danger" />
                        </button>
                        <button
                          onClick={() => openEdit(row)}
                          disabled={busy}
                          className="p-1 sm:p-1.5 rounded hover:bg-surface-high transition-colors disabled:opacity-40"
                          title="Edit and approve"
                        >
                          <EditIcon className="text-[12px] sm:text-[16px] text-primary" />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {totalPages > 1 && (
          <div className="flex flex-col sm:flex-row items-center justify-between gap-2 px-3 sm:px-4 py-3 border-t border-border-subtle">
            <span className="text-body-xs sm:text-body-sm text-on-surface-variant">
              Page {page}/{totalPages} ({total} total)
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(Math.max(1, page - 1))}
                disabled={page <= 1}
                className="w-8 h-8 flex items-center justify-center border border-border-subtle rounded-lg hover:bg-surface-low disabled:opacity-40 text-on-surface transition-colors"
              >
                <ArrowBackIcon className="text-[14px] sm:text-[16px]" />
              </button>
              <button
                onClick={() => setPage(Math.min(totalPages, page + 1))}
                disabled={page >= totalPages}
                className="w-8 h-8 flex items-center justify-center border border-border-subtle rounded-lg hover:bg-surface-low disabled:opacity-40 text-on-surface transition-colors"
              >
                <ArrowForwardIcon className="text-[14px] sm:text-[16px]" />
              </button>
            </div>
          </div>
        )}
      </div>

      {editing && typeof document !== 'undefined' && createPortal(
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-3 sm:p-4 bg-black/50 backdrop-blur-sm"
          onClick={() => setEditing(null)}
        >
          <div
            className="bg-surface rounded-xl shadow-xl w-full max-w-lg border border-border-subtle animate-toast-in"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-4 sm:px-5 py-3 sm:py-4 border-b border-border-subtle">
              <h3 className="font-headline-sm text-on-surface">Edit link</h3>
              <button onClick={() => setEditing(null)} className="p-1 rounded hover:bg-surface-high transition-colors">
                <CloseIcon className="text-on-surface-variant text-[20px]" />
              </button>
            </div>

            <div className="px-4 sm:px-5 py-4 space-y-3">
              <div>
                <label className="text-[10px] font-label-caps text-on-surface-variant uppercase block mb-1">URL</label>
                <input
                  type="text"
                  value={editUrl}
                  onChange={(e) => setEditUrl(e.target.value)}
                  placeholder="https://..."
                  className="w-full bg-surface-high border border-border-subtle rounded-lg px-3 py-2 text-body-xs sm:text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <div>
                <label className="text-[10px] font-label-caps text-on-surface-variant uppercase block mb-1">Price selector</label>
                <input
                  type="text"
                  value={editSelector}
                  onChange={(e) => setEditSelector(e.target.value)}
                  placeholder="CSS selector for the price"
                  className="w-full bg-surface-high border border-border-subtle rounded-lg px-3 py-2 text-body-xs sm:text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <div>
                <label className="text-[10px] font-label-caps text-on-surface-variant uppercase block mb-1">Stock selector</label>
                <input
                  type="text"
                  value={editStockSelector}
                  onChange={(e) => setEditStockSelector(e.target.value)}
                  placeholder="CSS selector for stock"
                  className="w-full bg-surface-high border border-border-subtle rounded-lg px-3 py-2 text-body-xs sm:text-body-sm text-on-surface placeholder:text-on-surface-variant/50 focus:outline-none focus:ring-2 focus:ring-primary/20"
                />
              </div>
              <p className="text-[10px] text-on-surface-variant">
                Saving approves the row. It is picked up by the next scrape pass, not scraped immediately.
              </p>
            </div>

            <div className="flex justify-end gap-2 px-4 sm:px-5 py-3 sm:py-4 border-t border-border-subtle">
              <button
                onClick={() => setEditing(null)}
                className="px-3 sm:px-4 py-2 rounded-lg bg-surface-high text-on-surface text-body-xs sm:text-body-sm font-semibold hover:opacity-90"
              >
                Cancel
              </button>
              <button
                onClick={saveEdit}
                disabled={busy}
                className="px-3 sm:px-4 py-2 rounded-lg bg-primary text-white text-body-xs sm:text-body-sm font-semibold hover:opacity-90 disabled:opacity-50 flex items-center gap-1.5"
              >
                <SaveIcon className="text-[14px] sm:text-[16px]" />
                Save &amp; approve
              </button>
            </div>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default Review;
